import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData, updatePaymentType } from '../../../apiService';
import { Link } from 'react-router-dom';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { generateBillPDF } from "../Sale/BillPrintPDF";
import CircularProgress from '@mui/material/CircularProgress';
import './SaleIndex.css';

function SaleIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [paymentChangeDialogOpen, setPaymentChangeDialogOpen] = useState(false);
    const [selectedSaleId, setSelectedSaleId] = useState(null);
    const [selectedNewPaymentType, setSelectedNewPaymentType] = useState(null);
    const [paymentUpdating, setPaymentUpdating] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(100);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        const today = getFormattedCurrentDate();
        setFromDate(today);
        setToDate(today);
    }, []);

    useEffect(() => {
        if (fromDate && toDate && isAuthenticated && companyId) {
            fetchTableData();
        }
    }, [fromDate, toDate]);

    const hasEditTransactionPermission = permissions.includes('Edit Transaction');
    const hasDeleteTransactionPermission = permissions.includes('Delete Transaction');

    const fetchTableData = async () => {
        try {
            setLoading(true);
            setData([]);
            setCurrentPage(1);
            const response = await fetchData('SaleApi', `/saleDetails/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Sale Details:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
    const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const handleDelete = async () => {
        try {
            const response = await deleteData('SaleApi', `${deleteItemId}`, token);
            if (response) {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            }
        } catch (error) {
            console.error('Error deleting record:', error);
        }
    };

    const handleActivate = async (itemId) => {
        try {
            await activateData('SaleApi', `${itemId}`, token);
            fetchTableData();
        } catch (error) {
            console.error('Error activating record:', error);
        }
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const openPaymentChangeDialog = (saleId, currentPaymentText) => {
        setSelectedSaleId(saleId);
        // Pre-select current payment type
        const type = currentPaymentText?.toLowerCase() || '';
        if (type.includes('cash')) setSelectedNewPaymentType('1');
        else if (type.includes('card')) setSelectedNewPaymentType('2');
        else setSelectedNewPaymentType('3');
        setPaymentChangeDialogOpen(true);
    };

    const closePaymentChangeDialog = () => {
        setSelectedSaleId(null);
        setSelectedNewPaymentType(null);
        setPaymentChangeDialogOpen(false);
    };

    const getPaymentTypeText = (paymentType) => {
        if (paymentType == 1) return 'Cash';
        if (paymentType == 2) return 'Card';
        if (paymentType == 3) return 'UPI';
        return '';
    };

    const handlePaymentTypeChange = async () => {
        if (!selectedNewPaymentType) return;
        try {
            setPaymentUpdating(true);
            await updatePaymentType('SaleApi', selectedSaleId, selectedNewPaymentType, token);
            closePaymentChangeDialog();
            await fetchTableData();
        } catch (error) {
            console.error('Error updating payment type:', error);
        } finally {
            setPaymentUpdating(false);
        }
    };

    const getPaymentBadgeClass = (paymentTypeText, paymentType) => {
        if (paymentType == 1) return 'payment-badge payment-badge-cash';
        if (paymentType == 2) return 'payment-badge payment-badge-card';
        if (paymentType == 3) return 'payment-badge payment-badge-upi';
        const type = paymentTypeText?.toLowerCase() || '';
        if (type.includes('cash')) return 'payment-badge payment-badge-cash';
        if (type.includes('card')) return 'payment-badge payment-badge-card';
        if (type.includes('upi')) return 'payment-badge payment-badge-upi';
        return 'payment-badge payment-badge-default';
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    }

    function formatTime(timeString) {
        const options = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
        return new Date(`1970-01-01T${timeString}`).toLocaleTimeString('en-GB', options);
    }

    const handlePrint = async (saleid) => {
        try {
            const response = await fetchData('SaleApi', `/SalePrint/${saleid}`, token);
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];
            for (const d of labelsToPrint) {
                await generateBillPDF(d);
            }
        } catch (error) {
            console.error('Error generating Invoice PDF:', error);
        }
    };

    const getFormattedCurrentDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const fmt = (val) => (val ? Number(val).toFixed(2) : '0.00');

    const startEntry = filteredData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const endEntry = Math.min(currentPage * rowsPerPage, filteredData.length);

    const pageTotal = paginatedData.reduce((sum, item) => sum + (item.TotalAmount || 0), 0);
    const pagePaymentTypes = [...new Set(paginatedData.map(item => item.PaymentType))];
    const allSamePayment = pagePaymentTypes.length === 1 ? pagePaymentTypes[0] : null;

    const getRowBg = (paymentType) => {
        if (paymentType == 1) return '#e5fdeeff';  // green-200
        if (paymentType == 2) return '#deebfbff';  // blue-200
        if (paymentType == 3) return '#dbd4f9ff';  // purple-200
        return '#ffffff';
    };

    const getRowHoverBg = (paymentType) => {
        if (paymentType == 1) return '#c7f8d9ff';  // green-300
        if (paymentType == 2) return '#c8e1feff';  // blue-300
        if (paymentType == 3) return '#dad3f7ff';  // purple-300
        return '#f3f4f6';
    };

    const totalRowBg =
        allSamePayment == 1 ? '#16a34a' :
        allSamePayment == 2 ? '#2563eb' :
        allSamePayment == 3 ? '#7c3aed' :
        '#1f2937';

    const cashTotal  = filteredData.filter(i => i.PaymentType == 1).reduce((s, i) => s + (i.TotalAmount || 0), 0);
    const cardTotal  = filteredData.filter(i => i.PaymentType == 2).reduce((s, i) => s + (i.TotalAmount || 0), 0);
    const upiTotal   = filteredData.filter(i => i.PaymentType == 3).reduce((s, i) => s + (i.TotalAmount || 0), 0);
    const grandTotal = cashTotal + cardTotal + upiTotal;

    return (
        <div className="sale-index-container">
            {/* Header */}
            <div className="sale-header-card">
                <div className="sale-header-content">
                    <div className="sale-title-section">
                        <span className="sale-title-icon"><ShoppingCartIcon fontSize="small" /></span>
                        <h1 className="sale-title">Sale Management</h1>
                    </div>

                    <div className="sale-controls">
                        <div className="date-filters">
                            <div className="date-input-group">
                                <label className="date-label">From</label>
                                <input
                                    className="date-input"
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>
                            <div className="date-input-group">
                                <label className="date-label">To</label>
                                <input
                                    className="date-input"
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className={`search-bar-wrapper${searchOpen ? ' search-bar-open' : ''}`}>
                            <span className="search-bar-icon" onClick={() => setSearchOpen(o => !o)} style={{ cursor: 'pointer' }}>
                                <SearchIcon fontSize="small" />
                            </span>
                            {searchOpen && (
                                <input
                                    autoFocus
                                    className="search-bar-input"
                                    type="text"
                                    placeholder="Search by Bill No, Customer..."
                                    value={searchQuery}
                                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                                />
                            )}
                        </div>

                        <button className="refresh-btn" onClick={fetchTableData}>
                            <RefreshIcon fontSize="small" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Payment Summary Strip */}
            {!loading && filteredData.length > 0 && (
                <div className="payment-summary-strip">
                    <div className="summary-chip summary-chip-cash">
                        <span className="summary-chip-label">💵 Cash</span>
                        <span className="summary-chip-amount">₹{cashTotal.toFixed(2)}</span>
                    </div>
                    <div className="summary-chip summary-chip-card">
                        <span className="summary-chip-label">💳 Card</span>
                        <span className="summary-chip-amount">₹{cardTotal.toFixed(2)}</span>
                    </div>
                    <div className="summary-chip summary-chip-upi">
                        <span className="summary-chip-label">📱 UPI</span>
                        <span className="summary-chip-amount">₹{upiTotal.toFixed(2)}</span>
                    </div>
                    <div className="summary-chip summary-chip-total">
                        <span className="summary-chip-label">🧾 Total</span>
                        <span className="summary-chip-amount">₹{grandTotal.toFixed(2)}</span>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="table-wrapper">
                <TableContainer component={Paper} className="table-container" elevation={0}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className="table-header-cell">Sl.No</TableCell>
                                <TableCell className="table-header-cell">Bill Date</TableCell>
                                <TableCell className="table-header-cell">Bill Time</TableCell>
                                <TableCell className="table-header-cell">Bill Number</TableCell>
                                <TableCell className="table-header-cell">Table Name</TableCell>
                                <TableCell className="table-header-cell">Payment Type</TableCell>
                                <TableCell className="table-header-cell" align="right">Amount</TableCell>
                                <TableCell className="table-header-cell" align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" className="loading-cell">
                                        <div className="loading-container">
                                            <CircularProgress size={36} sx={{ color: '#7c3aed' }} />
                                            <p className="loading-text">Loading sales data...</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : paginatedData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" className="no-data-cell">
                                        <p className="no-data-text">No sales data found</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedData.map((item, index) => (
                                    <TableRow
                                        key={item.Id}
                                        className={`table-row ${!item.IsActive ? 'inactive-row' : ''}`}
                                        sx={item.IsActive ? {
                                            backgroundColor: getRowBg(item.PaymentType),
                                            '&:hover': { backgroundColor: getRowHoverBg(item.PaymentType) }
                                        } : {}}
                                    >
                                        <TableCell className="table-cell">
                                            {(currentPage - 1) * rowsPerPage + index + 1}
                                        </TableCell>
                                        <TableCell className="table-cell">{formatDate(item.InvoiceDate)}</TableCell>
                                        <TableCell className="table-cell">{formatTime(item.InvoiceTime)}</TableCell>
                                        <TableCell className="table-cell table-cell-bold">{item.InvoiceNumber}</TableCell>
                                        <TableCell className="table-cell">{item.TableText}</TableCell>
                                        <TableCell className="table-cell">
                                            <span 
                                                className={getPaymentBadgeClass(item.PaymentTypeText, item.PaymentType)}
                                                onClick={() => openPaymentChangeDialog(item.Id, item.PaymentTypeText)}
                                                title="Click to change payment type"
                                            >
                                                {item.PaymentTypeText}
                                            </span>
                                        </TableCell>
                                        <TableCell className="table-cell amount-total" align="right">
                                            ₹{item.TotalAmount ? item.TotalAmount.toFixed(2) : '0.00'}
                                        </TableCell>
                                        <TableCell className="table-cell" align="center">
                                            <div className="action-buttons">
                                                <IconButton
                                                    onClick={() => handlePrint(item.Id)}
                                                    className="action-btn print-btn"
                                                    size="small"
                                                    title="Print"
                                                >
                                                    <PrintIcon sx={{ fontSize: 16 }} />
                                                </IconButton>                                                

                                                {hasEditTransactionPermission && (
                                                    <Link to={`/saleEdit/${item.Id}`}>
                                                        <IconButton className="action-btn edit-btn" size="small" title="Edit">
                                                            <EditIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Link>
                                                )}

                                                {hasDeleteTransactionPermission && (
                                                    <IconButton
                                                        onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))}
                                                        className={`action-btn ${item.IsActive ? 'delete-btn' : 'activate-btn'}`}
                                                        size="small"
                                                        title={item.IsActive ? 'Delete' : 'Activate'}
                                                    >
                                                        {item.IsActive ? <DeleteIcon sx={{ fontSize: 16 }} /> : <ActivateIcon sx={{ fontSize: 16 }} />}
                                                    </IconButton>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                            {/* Total Row */}
                            {!loading && paginatedData.length > 0 && (
                                <TableRow sx={{ backgroundColor: totalRowBg, '&:hover': { backgroundColor: totalRowBg } }}>
                                    <TableCell colSpan={6} className="total-row-label">
                                        Total ({paginatedData.length} bills)
                                    </TableCell>
                                    <TableCell className="total-row-amount" align="right">
                                        ₹{pageTotal.toFixed(2)}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <div className="pagination-wrapper">
                    <div className="pagination-info">
                        <span>
                            Showing {startEntry} to {endEntry} of {filteredData.length} entries &nbsp; Rows:
                        </span>
                        <select
                            className="rows-select"
                            value={rowsPerPage}
                            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        >
                            {[100, 150, 250, 500].map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>

                    <div className="pagination-controls">
                        <button className="page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
                        <button className="page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</button>
                        <span className="page-btn page-btn-active">{currentPage} / {totalPages}</span>
                        <button className="page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
                        <button className="page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
                    </div>
                </div>
            </div>

            {/* Delete Confirm Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                PaperProps={{ style: { borderRadius: '10px', padding: '4px' } }}
            >
                <DialogTitle className="dialog-title">Delete Record?</DialogTitle>
                <DialogContent>
                    <DialogContentText className="dialog-text">
                        Are you sure you want to delete this record? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions className="dialog-actions">
                    <button onClick={closeConfirmDialog} className="dialog-btn cancel-btn">Cancel</button>
                    <button onClick={handleDelete} className="dialog-btn confirm-btn">Confirm</button>
                </DialogActions>
            </Dialog>

            {/* Payment Type Change Confirm Dialog */}
            <Dialog
                open={paymentChangeDialogOpen}
                onClose={closePaymentChangeDialog}
                PaperProps={{ style: { borderRadius: '12px', padding: '8px', minWidth: '380px', maxWidth: '420px' } }}
            >
                <DialogTitle style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '10px',
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#1f2937',
                    paddingBottom: '12px'
                }}>
                    <WarningAmberIcon style={{ color: '#f59e0b', fontSize: '24px' }} />
                    Change Payment Type?
                </DialogTitle>
                <DialogContent style={{ paddingTop: '8px', paddingBottom: '16px' }}>
                    {/* Warning Message */}
                    <div style={{
                        background: '#fffbeb',
                        border: '1.5px solid #fcd34d',
                        borderRadius: '8px',
                        padding: '10px 12px',
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px'
                    }}>
                        <span style={{ fontSize: '16px', flexShrink: 0 }}>⚠️</span>
                        <span style={{ fontSize: '13px', color: '#92400e', lineHeight: '1.5' }}>
                            <strong>Warning:</strong> You are about to change the payment type for this transaction. This action will update the payment method and may affect reports.
                        </span>
                    </div>

                    {/* Payment Type Selector */}
                    <div style={{ marginBottom: '8px' }}>
                        <label style={{
                            display: 'block',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#6b7280',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '10px'
                        }}>
                            Select New Payment Type
                        </label>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {[
                                { label: 'CASH', value: '1', icon: '💵' },
                                { label: 'CARD', value: '2', icon: '💳' },
                                { label: 'UPI',  value: '3', icon: '📱' },
                            ].map((opt) => (
                                <div
                                    key={opt.value}
                                    className={`payment-option-card ${selectedNewPaymentType === opt.value ? 'selected' : ''}`}
                                    onClick={() => setSelectedNewPaymentType(opt.value)}
                                >
                                    <span className="payment-option-icon">{opt.icon}</span>
                                    <div className="payment-option-label">{opt.label}</div>
                                    {selectedNewPaymentType === opt.value && (
                                        <div className="payment-option-selected-text">✓ Selected</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </DialogContent>
                <DialogActions style={{ padding: '8px 20px 16px', gap: '10px' }}>
                    <button 
                        onClick={closePaymentChangeDialog} 
                        disabled={paymentUpdating}
                        style={{
                            flex: 1,
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: '2px solid #e5e7eb',
                            background: '#ffffff',
                            color: '#374151',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: paymentUpdating ? 'not-allowed' : 'pointer',
                            opacity: paymentUpdating ? 0.5 : 1,
                            transition: 'all 0.2s'
                        }}
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handlePaymentTypeChange}
                        disabled={paymentUpdating || !selectedNewPaymentType}
                        style={{
                            flex: 1,
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            background: paymentUpdating || !selectedNewPaymentType 
                                ? '#d1d5db' 
                                : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                            color: '#ffffff',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: paymentUpdating || !selectedNewPaymentType ? 'not-allowed' : 'pointer',
                            boxShadow: paymentUpdating || !selectedNewPaymentType 
                                ? 'none' 
                                : '0 4px 12px rgba(124, 58, 237, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            transition: 'all 0.2s'
                        }}
                    >
                        {paymentUpdating ? (
                            <>
                                <CircularProgress size={16} color="inherit" />
                                <span>Updating...</span>
                            </>
                        ) : (
                            <>
                                <span>✓</span>
                                <span>OK, Confirm</span>
                            </>
                        )}
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default SaleIndex;
